/**
 * Applies the document's track list to the engine by diffing (AGENT.md §6):
 * strips and instruments are created once; ordinary edits only set params.
 */
import type { AudioEngine } from "../audio/engine";
import { DrumKitInstrument } from "../audio/instruments/drumkit";
import { MultiSampleInstrument } from "../audio/instruments/multisample";
import { SamplerInstrument } from "../audio/instruments/sampler";
import { SynthInstrument } from "../audio/instruments/synth";
import type { Instrument } from "../audio/instruments/types";
import type { InstrumentConfig, Track } from "../project/schema";

export interface LiveTrack {
  id: string;
  kind: InstrumentConfig["kind"];
  instrument: Instrument;
  config: Track;
}

function createInstrument(ctx: BaseAudioContext, cfg: InstrumentConfig): Instrument {
  switch (cfg.kind) {
    case "synth": return new SynthInstrument(ctx, cfg.params);
    case "drumkit": return new DrumKitInstrument(ctx, cfg.params);
    case "sampler": return new SamplerInstrument(ctx, cfg.params);
    case "multisample": return new MultiSampleInstrument(ctx, cfg.params);
  }
}

/** What a track's audio data comes from (sample asset or built-in sound); null = nothing to load. */
function sourceKey(cfg: InstrumentConfig): string | null {
  if (cfg.kind === "sampler") return cfg.assetId;
  if (cfg.kind === "multisample") return `sound:${cfg.soundId}`;
  return null;
}

export class TrackGraph {
  private tracks = new Map<string, LiveTrack>();
  /** Counts instrument constructions — lets tests prove edits don't rebuild the graph. */
  instrumentsCreated = 0;

  constructor(private engine: AudioEngine) {}

  get(id: string): LiveTrack | undefined { return this.tracks.get(id); }
  all(): LiveTrack[] { return [...this.tracks.values()]; }

  /** Returns ids of tracks whose audio data must be (re)loaded: sampler asset or built-in sound changed. */
  apply(tracks: readonly Track[]): string[] {
    const changedAssets: string[] = [];
    const wanted = new Set(tracks.map((t) => t.id));
    for (const [id, live] of this.tracks) {
      if (!wanted.has(id)) {
        live.instrument.dispose();
        this.engine.mixer.remove(id);
        this.tracks.delete(id);
      }
    }
    for (const t of tracks) {
      const live = this.tracks.get(t.id);
      if (!live) {
        const strip = this.engine.mixer.add(t.id, t.mixer);
        const instrument = createInstrument(this.engine.ctx, t.instrument);
        this.instrumentsCreated++;
        instrument.output.connect(strip.input);
        this.tracks.set(t.id, { id: t.id, kind: t.instrument.kind, instrument, config: t });
        if (sourceKey(t.instrument)) changedAssets.push(t.id);
        continue;
      }
      if (live.config === t) continue;
      if (live.config.mixer !== t.mixer) this.engine.mixer.update(t.id, t.mixer);
      if (live.kind !== t.instrument.kind) {
        live.instrument.dispose();
        const instrument = createInstrument(this.engine.ctx, t.instrument);
        this.instrumentsCreated++;
        instrument.output.connect(this.engine.mixer.get(t.id)!.input);
        live.instrument = instrument;
        live.kind = t.instrument.kind;
        if (sourceKey(t.instrument)) changedAssets.push(t.id);
      } else if (live.config.instrument !== t.instrument) {
        if (live.config.instrument.params !== t.instrument.params) live.instrument.setParams(t.instrument.params as never);
        if (sourceKey(live.config.instrument) !== sourceKey(t.instrument)) changedAssets.push(t.id);
      }
      live.config = t;
    }
    return changedAssets;
  }

  releaseAll(time?: number): void { for (const t of this.tracks.values()) t.instrument.releaseAll(time); }
  hardStop(): void { for (const t of this.tracks.values()) t.instrument.hardStop(); }

  dispose(): void {
    for (const t of this.tracks.values()) t.instrument.dispose();
    this.tracks.clear();
  }
}
